import { useCallback, useEffect, useState } from 'react';
import { AssetsService } from '../../../services/assetsService';
import { useNotifications } from '../../../hooks/useNotifications';
import { debounceFunction } from '../../../utility/utility';
import { ApplicationsService } from '../../../services/applicationsService';
import { useDictionaryTranslation } from '../../../hooks/useDictionaryTranslation';

export const useAsset = ({
  assetId,
  createAssetFunction = AssetsService.createAsset,
  updateAssetFunction = AssetsService.updateAsset,
}) => {
  const [assetData, setAssetData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [dataExtendedForm, setDataExtendedForm] = useState();
  const { showSuccessNotification, showErrorNotification } = useNotifications();
  const { translate: t } = useDictionaryTranslation();

  const errorMessages = {
    [AssetsService.ASSET_REFERENCE_ALREADY_EXISTS]: 'assetReferenceAlreadyExists',
  };

  const checkError = (message) => {
    if (message == null) {
      return false;
    }
    const errorMessage = errorMessages[message];
    if (errorMessage == null) {
      return false;
    }
    showErrorNotification({
      title: 'error',
      description: t(errorMessage),
    });
    return true;
  };

  const getData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { asset } = await AssetsService.getAssetById(assetId);
      setAssetData(asset);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [assetId]);

  const updateAsset = useCallback(
    async (data, newAssetId, showMessage = true) => {
      setIsLoading(true);
      let result = null;
      try {
        result = await updateAssetFunction(newAssetId ?? assetId, data);
        if (showMessage) {
          showSuccessNotification({
            title: 'success',
            description: 'successUpdatingRecord',
          });
        }
      } catch (error) {
        showErrorNotification({
          title: 'error',
          description: 'errorUpdatingRecord',
        });
      } finally {
        setIsLoading(false);
      }
      return result;
    },
    [assetId],
  );

  const createAsset = useCallback(
    async (data) => {
      setIsLoading(true);
      let result = null;
      try {
        result = await createAssetFunction(data);
        showSuccessNotification({
          title: 'success',
          description: 'successCreatingRecord',
        });
      } catch (error) {
        showErrorNotification({
          title: 'error',
          description: 'errorCreatingRecord',
        });
      } finally {
        setIsLoading(false);
      }
      return result;
    },
    [assetId],
  );

  const getFormExtended = useCallback(
    debounceFunction(async (idApp) => {
      setIsLoading(true);
      try {
        const { application } = await ApplicationsService.getFormById(idApp);
        setDataExtendedForm(application);
      } catch (error) {
        console.error(error);
        showErrorNotification({
          title: 'error',
          description: 'errorRetrievingRecord',
        });
      } finally {
        setIsLoading(false);
      }
    }, 200),
    [],
  );

  useEffect(() => {
    if (assetId) {
      getData();
    }
  }, [getData, assetId]);

  return {
    assetData,
    isLoading,
    updateAsset,
    getFormExtended,
    dataExtendedForm,
    createAsset,
    checkError,
  };
};
